import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllCubeTestReports, printCubeTestReport } from "../../services/CubeTestReportServices";
import { Row, Col, Button } from "reactstrap";
import { DatePicker } from 'antd';
import moment from 'moment';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { displayError } from "../../helpers";
import { mapAllowedPlants, pickDefaultPlantId } from './cubeTestReportDefaults';

const { RangePicker } = DatePicker;

function CubeTestReportTable() {
    const InitialData = {
        records: []
    };
    const [data, setData] = useState(InitialData);
    const [inputs, setInputs] = useState({});
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);
    const [plants, setPlants] = useState([]);
    const initialFromDate = moment().startOf('month');
    const initialToDate = moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);

    const summaryTotals = useMemo(() => ({
        total_count: data.records.length,
        active_count: data.records.filter((row) => row.status === 'Active').length,
    }), [data.records]);

    const todayReportCard = useMemo(() => ({
        label: 'Today Reports',
        value: String(data.records.filter((row) => row.report_date === moment().format('DD-MM-YYYY')).length),
        gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #5EC8F0 100%)',
        shadow: '0 8px 20px rgba(0, 144, 216, 0.28)',
    }), [data.records]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Total',
            value: String(summaryTotals.total_count),
            gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
        {
            label: 'Active',
            value: String(summaryTotals.active_count),
            gradient: 'linear-gradient(135deg, #305870 0%, #186098 55%, #5AA8D8 100%)',
            shadow: '0 8px 20px rgba(48, 88, 112, 0.24)',
        },
    ]), [summaryTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'plant_alias', headerName: 'Plant' },
            { fieldName: "report_no", headerName: 'Report Number' },
            { fieldName: "report_date", headerName: 'Report Date' },
            { fieldName: "casting_date", headerName: 'Casting Date' },
            { fieldName: "customer_name", headerName: 'Customer Name' },
            { fieldName: "project_name", headerName: 'Site Name' },
            { fieldName: "grade", headerName: 'Grade' },
            { fieldName: 'salesrep_name', headerName: 'Marketing' },
            { fieldName: "status", headerName: 'Status' },
            { fieldName: "id", headerName: "ROW_ACTIONS" }
        ],
        [],
    );

    const handleDateChange = (values) => {
        if (values && values.length === 2) {
            setFromDate(values[0]);
            setToDate(values[1]);
        }
    };

    const onEditCubeTestReport = (ctrId) => {
        navigate(`/CubeTestReportEdit/${ctrId}/`);
    };

    const onPrintCubeTestReport = (ctrId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printCubeTestReport(cookies, ctrId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Compressive Strength Report',
                    downloadFileName: 'compressive_strength_report.pdf',
                });
            })
            .catch(async (error) => {
                let payload = error?.response?.data ?? error;
                if (typeof Blob !== 'undefined' && payload instanceof Blob) {
                    try {
                        payload = JSON.parse(await payload.text());
                    } catch (e) {
                        payload = { message: 'Print failed.' };
                    }
                }
                displayError(payload, 'Print Failed');
            })
            .finally(() => setIsLoading(false));
    };

    const mapRecords = (response) => response.cube_test_report_list.map((ctr) => ({
        ...ctr,
        plant_alias: ctr.plant.alias_name,
        report_no: ctr.prefix + ctr.report_no,
        customer_name: ctr.consignee.customer.name,
        salesrep_name: ctr.consignee.customer.salesrep.alias
          || ctr.consignee.customer.salesrep.name,
        consignee_name: ctr.consignee.name,
        project_name: ctr.consignee.project_name,
        status: ctr.status ? 'Active' : 'Inactive',
    }));

    const loadReports = useCallback(() => {
        if (!inputs.plant_id || !fromDate || !toDate) {
            return;
        }
        setIsLoading(true);
        getAllCubeTestReports(cookies, {
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
            plant_id: inputs.plant_id,
        })
            .then((response) => {
                setData({ records: mapRecords(response) });
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error, 'Failed to load reports');
            })
            .finally(() => setIsLoading(false));
    }, [cookies, fromDate, inputs.plant_id, toDate]);

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((ctr) => ({
                plant_alias: ctr.plant_alias,
                report_no: ctr.report_no,
                report_date: ctr.report_date,
                casting_date: ctr.casting_date,
                customer_name: ctr.customer_name,
                project_name: ctr.project_name,
                grade: ctr.grade,
                salesrep_name: ctr.salesrep_name,
                status: ctr.status,
            })),
        );
    };

    const handleChange = (event) => {
        const name = event.target.name;
        setInputs((values) => ({ ...values, [name]: event.target.value }));
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Compressive Strength Reports',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((ctr) => ({
                plant_alias: ctr.plant_alias,
                report_no: ctr.report_no,
                report_date: ctr.report_date,
                casting_date: ctr.casting_date,
                customer_name: ctr.customer_name,
                project_name: ctr.project_name,
                grade: ctr.grade,
                salesrep_name: ctr.salesrep_name,
                status: ctr.status,
            })),
            'CompressiveStrengthReports.pdf',
        );
    };

  useEffect(() => {
    const pendingPlantId = searchParams.get('plantId');
    getUserAllowedPlants(cookies)
      .then((plantList) => {
        const plantOptions = mapAllowedPlants(plantList);
        setPlants(plantOptions);
        const defaultPlantId = pendingPlantId || pickDefaultPlantId(plantOptions);
        if (defaultPlantId) {
          setInputs({ plant_id: defaultPlantId });
        }
      });
  }, [cookies, searchParams]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (inputs.plant_id && fromDate && toDate) {
            loadReports();
        } else {
            Swal.fire('Plant, from date, and to date must not be empty.', '', 'info');
        }
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/CubeTestReport"
                    addNewLabel="New Report"
                    homeLink="/Home"
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                />
                <div className="container">
                    <PremiumPageHeader
                        module="QUALITY CONTROL"
                        title="List of Compressive Strength Report"
                        subtitle="Filter by plant and date range, then search to load cube test reports."
                        modeLabel="VIEW MODE"
                    />
                </div>

                <form className="form" onSubmit={handleSubmit}>
                    <div className="container">
                        <Row>
                            <Col xl={12} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue form-control-panel">
                                    <label htmlFor="plant_id" className="form-group col-sm-2 text-right">Plant</label>
                                    <select
                                        id="plant_id"
                                        className="browser-default custom-select col-sm-4 mandatory-form-control"
                                        onChange={handleChange}
                                        required
                                        name="plant_id"
                                        value={inputs.plant_id || ''}
                                    >
                                        <option value="">Select Plant Location</option>
                                        {plants.map((item) => (
                                            <option key={item.value} value={item.value}>{item.label}</option>
                                        ))}
                                    </select>
                                    <label className="form-group col-sm-1 text-right">Period</label>
                                    <RangePicker
                                        defaultValue={[initialFromDate, initialToDate]}
                                        format="DD-MM-YYYY"
                                        onChange={handleDateChange}
                                        style={{ height: '40px', width: '270px' }}
                                    />
                                    <div className="col-sm-4" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button
                                            type="submit"
                                            style={{
                                                backgroundColor: 'RGB(58,29,238,0.8)',
                                                fontWeight: 'bold',
                                                color: '#fff',
                                                width: '100px',
                                            }}
                                        >
                                            Search
                                        </Button>
                                        <Button
                                            type="button"
                                            style={{ fontWeight: 'bold' }}
                                            onClick={() => {
                                                const plantQuery = inputs.plant_id ? `&plantId=${inputs.plant_id}` : '';
                                                navigate(`/CubeTestReport?pendingAge=7${plantQuery}`);
                                            }}
                                        >
                                            7 Days Pending
                                        </Button>
                                        <Button
                                            type="button"
                                            style={{ fontWeight: 'bold' }}
                                            onClick={() => {
                                                const plantQuery = inputs.plant_id ? `&plantId=${inputs.plant_id}` : '';
                                                navigate(`/CubeTestReport?pendingAge=28${plantQuery}`);
                                            }}
                                        >
                                            28 Days Pending
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </form>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        mb: 2.5,
                        py: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: todayReportCard.gradient,
                                boxShadow: todayReportCard.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                {todayReportCard.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                {todayReportCard.value}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.25,
                            flex: 1,
                            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        {summaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    flex: '0 0 auto',
                                    minWidth: 128,
                                    borderRadius: '12px',
                                    background: card.gradient,
                                    boxShadow: card.shadow,
                                    px: 1.75,
                                    py: 1.1,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF COMPRESSIVE STRENGTH REPORT"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditCubeTestReport}
                            hideDelete
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintCubeTestReport}
                            hidePrint={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CubeTestReportTable;
